'use client';

import { getSupabaseClient } from '@popow/infra/lib/supabase/client';
import * as Sentry from '@sentry/nextjs';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Analytics } from '@/features/common/analytics';
import { createPostAuthNextCookie } from '@/features/common/config/authCookies';
import { HOME_PATH } from '@/features/common/config/routePaths';
import { ChevronLeftIcon } from '@/features/common/ui/icons/ChevronLeftIcon';
import { dbLogger } from '@/features/common/utils/dbLogger';
import { useTikTokPending } from '@/features/tiktok';

export type EmailFormViewState = 'enter-email' | 'enter-otp';

// TikTok 데이터 타입 (VerifyEmailPage에서 전달받음)
interface TiktokDataProp {
  open_id: string;
  union_id?: string;
  display_name?: string;
  avatar_url?: string;
  username?: string;
  scopes?: string[];
  // tiktok_handle로 기존 계정을 찾은 경우 (이메일 인증 필요)
  prefilled_email?: string; // 기존 계정의 이메일 (prefill용)
  email_locked?: boolean; // true면 이메일 수정 불가
  existing_user_id?: string; // 연동할 기존 계정 ID
}

interface EmailLoginFormProps {
  belowEmailSubmit?: React.ReactNode;
  onViewChange?: (view: EmailFormViewState) => void;
  redirectUrl?: string;
  tiktokData?: TiktokDataProp | null;
  /** 외부에서 view 상태를 제어할 때 사용 (controlled mode) */
  view?: EmailFormViewState;
  /** 내부 헤더(뒤로가기 버튼) 표시 여부 (기본값: true) */
  showInternalHeader?: boolean;
}

export default function EmailLoginForm({
  belowEmailSubmit,
  onViewChange,
  redirectUrl,
  tiktokData: tiktokDataProp,
  view: externalView,
  showInternalHeader = true,
}: EmailLoginFormProps) {
  const { t } = useTranslation();
  const supabase = getSupabaseClient();
  const { data: pendingTiktokData } = useTikTokPending();

  // Controlled mode: 외부에서 view를 제어하면 내부 상태 무시
  const [internalView, setInternalView] = useState<EmailFormViewState>('enter-email');
  const view = externalView ?? internalView;

  const setView = (newView: EmailFormViewState) => {
    if (externalView === undefined) {
      setInternalView(newView);
    }
    onViewChange?.(newView);
  };

  // tiktok_handle로 기존 계정을 찾은 경우, 이메일 prefill + locked
  const isEmailLocked = tiktokDataProp?.email_locked === true;
  const [email, setEmail] = useState(tiktokDataProp?.prefilled_email || '');

  // verify-email 페이지에서 URL/sessionStorage에서 비동기로 tiktokData를 로드하므로,
  // 컴포넌트 마운트 후 props가 업데이트되면 email state도 동기화
  useEffect(() => {
    if (tiktokDataProp?.prefilled_email && !email) {
      setEmail(tiktokDataProp.prefilled_email);
    }
  }, [tiktokDataProp?.prefilled_email, email]);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);

  // 연타 방지를 위한 즉각적인 잠금 (React 상태 업데이트는 비동기이므로 ref 사용)
  const isSubmittingRef = useRef(false);

  // Countdown effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (countdown !== null && countdown > 0) {
      interval = setInterval(() => {
        setCountdown(prev => {
          if (prev === null || prev <= 1) {
            setError(null);
            return null;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [countdown]);

  // Parse error message for rate limiting
  const parseRateLimitError = (errorMessage: string): number | null => {
    const match = errorMessage.match(/after (\d+) seconds?/i);
    return match ? parseInt(match[1], 10) : null;
  };

  const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // 즉각적인 잠금 체크 (연타 방지)
    if (loading || isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;

    Analytics.track('Start Login', {
      source: 'LoginPage',
      email_domain: email.split('@')[1] || 'unknown',
    });
    setLoading(true);
    setError(null);
    setCountdown(null);

    try {
      // Set short-lived cookie to preserve next
      if (redirectUrl) {
        try {
          // biome-ignore lint/suspicious/noDocumentCookie: OAuth flow requires direct cookie access
          document.cookie = createPostAuthNextCookie(redirectUrl);
        } catch {
          // Ignore cookie setting errors
        }
      }

      // OTP 방식: 6자리 코드 전송 (redirectTo 없음)
      // 'link' 모드: TikTok으로 이미 인증된 사용자가 이메일을 연동하는 흐름
      // 신규 사용자도 OTP를 받을 수 있도록 shouldCreateUser: true 설정
      // (TikTok OAuth로 인증했지만 이메일이 없는 신규 사용자도 포함)
      const { error: signInError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
        },
      });

      if (signInError) {
        throw signInError;
      }

      // OTP 입력 화면으로 전환
      setView('enter-otp');
      onViewChange?.('enter-otp');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';

      // Rate limit 에러는 Sentry에 보내지 않음
      if (!errorMessage.includes('after') && !errorMessage.includes('Signups not allowed')) {
        Sentry.captureException(error instanceof Error ? error : new Error(errorMessage), {
          tags: { flow: 'tiktok-login', step: 'email-submit' },
          extra: { hasTiktokData: !!tiktokDataProp },
        });
      }

      if (errorMessage.includes('Signups not allowed')) {
        setError('NOT_FOUND');
      } else {
        const waitTime = parseRateLimitError(errorMessage);
        if (waitTime) {
          setCountdown(waitTime);
          setError(`For security purposes, you can only request this after ${waitTime} seconds.`);
        } else {
          setError(errorMessage);
        }
      }
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError(null);

    try {
      if (redirectUrl) {
        try {
          // biome-ignore lint/suspicious/noDocumentCookie: Required for auth redirect
          document.cookie = createPostAuthNextCookie(redirectUrl);
        } catch {
          // Ignore cookie setting errors
        }
      }

      // 'link' 모드: TikTok으로 이미 인증된 사용자가 이메일을 연동하는 흐름
      // 신규 사용자도 OTP를 받을 수 있도록 shouldCreateUser: true 설정
      // (TikTok OAuth로 인증했지만 이메일이 없는 신규 사용자도 포함)
      const { error: resendError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
        },
      });

      if (resendError) {
        throw resendError;
      }

      setCountdown(null);
      setError(null);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resend code';

      // Rate limit 에러는 Sentry에 보내지 않음
      if (!errorMessage.includes('after')) {
        Sentry.captureException(error instanceof Error ? error : new Error(errorMessage), {
          tags: { flow: 'tiktok-login', step: 'resend-code' },
        });
      }

      const waitTime = parseRateLimitError(errorMessage);
      if (waitTime) {
        setCountdown(waitTime);
        setError(`For security purposes, you can only request this after ${waitTime} seconds.`);
      } else {
        setError(errorMessage);
      }
    }
    setLoading(false);
  };

  const handleOtpSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    if (otp.length < 6) {
      setError('Please enter the complete 6-digit verification code.');
      return;
    }

    setLoading(true);
    setError(null);

    // ⚠️ 중요: OTP 검증 전에 TikTok 데이터를 먼저 읽어둠
    // verifyOtp() 호출 시 onAuthStateChange가 트리거되면서 sessionStorage가 삭제될 수 있음
    const tiktokDataToUse = tiktokDataProp || pendingTiktokData;

    try {
      // OTP 검증
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: otp.trim(),
        type: 'email',
      });

      if (error) {
        // Token expired/invalid 에러는 사용자 실수이므로 Sentry 리포팅 제외
        const isTokenError =
          error.message.includes('Token has expired') || error.message.includes('invalid');

        if (!isTokenError) {
          Sentry.captureException(error, {
            tags: { flow: 'tiktok-login', step: 'otp-verify' },
            extra: { hasTiktokData: !!tiktokDataToUse, errorMessage: error.message },
          });
        }

        setError(error.message);
        setLoading(false); // 에러 발생 시에만 로딩 해제
      } else if (data.session) {
        // OTP 검증 성공
        // 📊 Analytics: Complete Login/Signup 이벤트는 useIdentifyUserOnLoad 훅에서 trackSignIn()을 통해 처리
        // - 리다이렉트 후 다음 페이지에서 AuthStoreInitializer가 user를 로드
        // - useIdentifyUserOnLoad가 user 변경 감지 → trackSignIn() 호출
        // - 여기서 직접 호출하면 중복 이벤트 발생하므로 제거됨

        // TikTok 데이터가 있으면 Welcome 페이지로 이동 (TikTok 연동 완료를 위해)
        if (tiktokDataToUse) {
          // 🔐 핸들 비교: 기존 프로필의 TikTok 핸들과 OAuth 핸들이 다르면 차단
          const { data: profile } = await supabase
            .from('user_profiles')
            .select('tiktok_handle')
            .eq('id', data.session.user.id)
            .single();

          if (profile?.tiktok_handle) {
            // 핸들 정규화 (@가 있으면 그대로, 없으면 추가)
            const oauthHandle = tiktokDataToUse.username?.startsWith('@')
              ? tiktokDataToUse.username
              : `@${tiktokDataToUse.username}`;

            if (profile.tiktok_handle !== oauthHandle) {
              dbLogger.error('auth', 'TikTok handle mismatch detected', {
                email,
                userId: data.session.user.id,
                existingHandle: profile.tiktok_handle,
                oauthHandle,
              });

              Sentry.captureMessage('TikTok handle mismatch - possible account conflict', {
                level: 'warning',
                tags: { flow: 'tiktok-login', step: 'handle-verification' },
                extra: { existingHandle: profile.tiktok_handle, oauthHandle },
              });

              // 세션 삭제 후 에러 페이지로
              await supabase.auth.signOut();
              window.location.href = '/login?error=tiktok_account_mismatch';
              return;
            }
          }

          // 기존 유저 (프로필 있음): /welcome 대신 직접 TikTok 연동
          // /welcome 이동 시 세션 쿠키 동기화 문제로 서버 액션이 Unauthorized 됨
          if (profile) {
            try {
              const { createProfileAction } = await import('@/features/profile/actions');
              const result = await createProfileAction({
                tiktokData: tiktokDataToUse,
                accessToken: data.session.access_token,
              });

              if (result.error && !result.error.includes('already exists')) {
                dbLogger.error('auth', 'Direct TikTok linking failed', {
                  error: result.error,
                  userId: data.session.user.id,
                });
                window.location.href = result.error.includes('Unauthorized')
                  ? '/login'
                  : '/welcome';
                return;
              }
            } catch (err) {
              dbLogger.error('auth', 'Direct TikTok linking error', {
                error: err instanceof Error ? err.message : String(err),
                userId: data.session.user.id,
              });
              window.location.href = '/login';
              return;
            }

            window.location.href = redirectUrl || HOME_PATH;
            return;
          }

          // 신규 유저 (프로필 없음): /welcome으로 이동
          // sessionStorage에 저장 (Welcome 페이지에서 사용)
          // setPendingTiktokData(tiktokDataToUse); // Already in store if loaded

          // 🔒 user_metadata에도 open_id 저장 (sessionStorage 유실 대비)
          try {
            await supabase.auth.updateUser({
              data: {
                pending_tiktok_open_id: tiktokDataToUse.open_id,
              },
            });
            dbLogger.info('auth', 'Saved pending_tiktok_open_id to user_metadata', {
              userId: data.session.user.id,
              openId: tiktokDataToUse.open_id,
            });
          } catch (metadataError) {
            dbLogger.warn('auth', 'Failed to save pending_tiktok_open_id to user_metadata', {
              error: metadataError instanceof Error ? metadataError.message : String(metadataError),
            });
          }

          dbLogger.info('auth', 'New user, redirecting to welcome page', {
            email,
            userId: data.session.user.id,
            openId: tiktokDataToUse.open_id,
            source: tiktokDataProp ? 'props' : 'sessionStorage',
          });

          window.location.href = '/welcome';
        } else {
          // TikTok 데이터가 없는 경우 (일반 이메일 로그인)
          const targetUrl = redirectUrl || HOME_PATH;
          window.location.href = targetUrl;
        }
      } else {
        setError('Login failed. Please try again.');
        setLoading(false); // 실패 시 로딩 해제
      }
    } catch (error) {
      Sentry.captureException(error instanceof Error ? error : new Error('OTP submit error'), {
        tags: { flow: 'tiktok-login', step: 'otp-submit' },
        extra: { hasTiktokData: !!tiktokDataToUse },
      });
      setError(error instanceof Error ? error.message : 'An error occurred');
      setLoading(false); // 에러 발생 시 로딩 해제
    }
  };

  const getErrorMessage = (_errorMsg: string | null, countdown: number | null): React.ReactNode => {
    if (countdown !== null && countdown > 0) {
      return `For security purposes, you can only request this after ${countdown} seconds.`;
    }

    if (error === 'NOT_FOUND') {
      return <span>We couldn&apos;t find that email. Please check the address and try again.</span>;
    }

    return error;
  };

  const handleBackToEmail = () => {
    setView('enter-email');
    setError(null);
    setOtp('');
    setCountdown(null);
  };

  if (view === 'enter-otp') {
    return (
      <form onSubmit={handleOtpSubmit} className="w-full space-y-2">
        {/* Back header for code verification step - 부모에서 Header 사용 시 숨김 */}
        {showInternalHeader && (
          <header className="sticky top-0 z-10 -mx-5 bg-semantic-bg-normal-normal px-2 pb-2">
            <div className="flex items-center justify-between py-2.5">
              <button
                type="button"
                onClick={handleBackToEmail}
                aria-label="go back"
                className="flex h-11 w-11 items-center justify-center"
              >
                <ChevronLeftIcon className="h-6 w-6 text-semantic-label-strong" />
              </button>
              <h2 className="absolute left-1/2 -translate-x-1/2 font-mona-sans text-heading-2-medium text-semantic-label-strong">
                {t('Login.VerifyEmailTitle', 'Verify your email')}
              </h2>
            </div>
          </header>
        )}
        <p className="mb-2 font-mona-sans text-label-1-normal-regular text-semantic-label-strong leading-snug">
          We&apos;ve sent a 6-digit code to <strong>{email}</strong>.
        </p>
        <div>
          <label
            htmlFor="otp"
            className="mb-2.5 block font-mona-sans text-label-1-normal-medium text-semantic-label-neutral"
          >
            Verification Code
          </label>
          <input
            id="otp"
            type="text"
            value={otp}
            onChange={e => setOtp(e.target.value)}
            className="w-full rounded-lg border border-semantic-line-normal-normal bg-semantic-bg-normal-alternative px-5 py-4 text-center tracking-[0.5em] placeholder-semantic-label-neutral transition focus:border-semantic-primary-normal focus:ring-semantic-primary-normal"
            placeholder="******"
            required={true}
            maxLength={6}
            pattern="[0-9]*"
          />
        </div>
        {error && (
          <div className="rounded-lg bg-semantic-status-destructive/10 p-3 text-center font-mona-sans text-label-1-normal-regular text-semantic-status-destructive">
            {error}
          </div>
        )}
        <div className="pt-3">
          <Button
            type="submit"
            disabled={loading || otp.length < 6}
            variant="cta-solid-alternative"
            isLoading={loading}
          >
            {loading ? 'Verifying...' : 'Verify and Continue'}
          </Button>
        </div>
        {/* Resend code button */}
        <div className="pt-2 text-center">
          <button
            type="button"
            onClick={handleResendCode}
            disabled={loading}
            className="font-mona-sans text-label-1-normal-regular text-semantic-label-neutral transition hover:text-semantic-label-strong disabled:cursor-not-allowed disabled:text-semantic-label-assistive"
          >
            {loading ? 'Sending...' : "Didn't receive code? Resend"}
          </button>
          {countdown !== null && countdown > 0 && (
            <p className="mt-1 font-mona-sans text-caption-1-regular text-semantic-label-assistive">
              Rate limited. Please wait {countdown}s before trying again.
            </p>
          )}
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleEmailSubmit} className="w-full space-y-2">
      <p className="mb-1 font-mona-sans text-label-1-normal-regular text-semantic-label-strong leading-snug">
        Email address
      </p>
      <div>
        <input
          id="email"
          type="email"
          value={email}
          onChange={e => !isEmailLocked && setEmail(e.target.value)}
          disabled={isEmailLocked}
          className={`w-full rounded-lg border px-5 py-4 placeholder-semantic-label-neutral transition ${
            isEmailLocked
              ? 'cursor-not-allowed border-semantic-line-normal-alternative bg-neutral-100 text-neutral-500 opacity-70'
              : 'border-semantic-line-normal-normal bg-semantic-bg-normal-alternative focus:border-semantic-primary-normal focus:ring-semantic-primary-normal'
          }`}
          placeholder="Enter your email address"
          required={true}
        />
        {isEmailLocked && (
          <p className="mt-2 font-mona-sans text-caption-1-regular text-semantic-label-assistive">
            This email is linked to your TikTok account. Verify to continue.
          </p>
        )}
      </div>
      {error && (
        <div className="rounded-lg bg-semantic-status-destructive/10 p-3 text-center font-mona-sans text-label-1-normal-regular text-semantic-status-destructive">
          {getErrorMessage(error, countdown)}
        </div>
      )}
      <div className="flex flex-col gap-4 pt-4">
        <Button
          type="submit"
          variant="cta-solid-alternative"
          isLoading={loading}
          disabled={loading}
        >
          {loading ? 'Sending...' : 'Send verification code'}
        </Button>
        {belowEmailSubmit}
      </div>
    </form>
  );
}
